import { useContext, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'
import logo from '../assets/logo.svg'

import hamburgerIcon from '../assets/hamburger.svg'
import logOutIcon from '../assets/logout.svg'
import backNavArrow from '../assets/back-nav-arrow.svg'
import { AuthContext } from '../contexts/Auth'
import { contentWidth } from './common/common'
import { redirectToLogin } from '../utils/loginRedirect'
import { hasRole, Role } from '../utils/roles'

const Row = styled.div`
  ${contentWidth}
  display: flex;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 50px;

  @media only screen and (max-width: 900px) {
    display: none;
  }
`

const Logo = styled.img`
  margin-right: auto;
`

const StyledLink = styled(Link)`
  text-decoration: none;
`

const Button = styled.span<{ active: boolean }>`
  padding: 5px 10px;
  color: ${({ active }) => (active ? 'white' : '#828493')};
  border-radius: 10px;
  font-weight: bold;
  cursor: pointer;
  margin-right: 10px;
`

const SignOutButton = styled.span`
  cursor: pointer;
  font-weight: 600;
  background: linear-gradient(180deg,#ff83af 0%,#f45a90 100%);
  box-shadow: 0px 5px 10px rgba(0,0,0,0.25);
  border-radius: 30px;
  margin-left: auto;
  width: 120px;
  height: 50px;
  display: block;
  text-align: center;
  line-height: 50px;
`

const MobileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  background: #2e2e2e;
  padding: 0 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  @media only screen and (min-width: 901px) {
    display: none;
  }
`

// 44px square keeps the icons comfortably tappable
const MobileIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  background: transparent;
  cursor: pointer;
`

const MobileLogo = styled.img`
  height: 30px;
`

const MobileNavPopout = styled.nav`
  background: #2e2e2e;
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  @media only screen and (min-width: 901px) {
    display: none;
  }
`

const popoutItem = css<{ $active: boolean }>`
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 16px;
  color: white;
  opacity: ${({ $active }) => ($active ? 1 : 0.7)};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
`

const PopoutLink = styled(Link)<{ $active: boolean }>`
  ${popoutItem}
`

const ForceRefesh = styled.span<{ $active: boolean }>`
  ${popoutItem}
`

// Admin pages, each shown from its minimum role up. Add staff pages with min: 'staff'.
const ADMIN_PAGES: { path: string; label: string; min: Role }[] = [
  { path: '/admin/droplists', label: 'Droplists', min: 'admin' },
  { path: '/admin/affiliates', label: 'Affiliates', min: 'admin' },
  { path: '/admin/releases', label: 'Releases', min: 'admin' },
]

const MenuWrap = styled.div`
  position: relative;
  margin-right: 10px;
`

const MenuButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  font-weight: bold;
  color: ${({ $active }) => ($active ? 'white' : '#828493')};
  cursor: pointer;

  &::after {
    content: '';
    width: 6px;
    height: 6px;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: translateY(-2px) rotate(45deg);
  }
`

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 20;
  min-width: 180px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  background: #23243a;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
`

const MenuLink = styled(Link)<{ $active: boolean }>`
  padding: 10px 12px;
  border-radius: 10px;
  color: ${({ $active }) => ($active ? 'white' : 'rgba(255, 255, 255, 0.75)')};
  background: ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.08)' : 'transparent')};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: white;
  }
`

const PopoutHeading = styled.p`
  padding: 12px 16px 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
`

/** Desktop dropdown listing the admin/staff pages; closes on outside click, Escape or navigation. */
const AdminMenu = ({ label, pages }: { label: string; pages: typeof ADMIN_PAGES }) => {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)

  useEffect(() => setOpen(false), [location.pathname])
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const active = pages.some((page) => location.pathname.startsWith(page.path))

  return (
    <MenuWrap ref={wrap}>
      <MenuButton type="button" $active={active || open} aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(!open)}>
        {label}
      </MenuButton>
      {open && (
        <Menu role="menu">
          {pages.map((page) => (
            <MenuLink key={page.path} role="menuitem" to={page.path} $active={location.pathname.startsWith(page.path)}>
              {page.label}
            </MenuLink>
          ))}
        </Menu>
      )}
    </MenuWrap>
  )
}

interface NavProps {
  onClose?: () => void
  modalNav?: boolean
}

const Nav = ({ modalNav, onClose }: NavProps) => {
  const location = useLocation()
  const authContext = useContext(AuthContext)

  const signOutHandler = () => {
    document.cookie = "swiftsole=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    redirectToLogin()
  }

  const loginHandler = () => {
    redirectToLogin()
  }

  const [navOpen, setNavOpen] = useState(false)

  // Any signed-in user can open the Affiliate tab; non-affiliates see the pitch while applications are open.
  const userInfo = authContext.userInfo || {}
  const affiliateSummary = userInfo.affiliate
  const showAffiliate =
    !!userInfo.id && (!!affiliateSummary || !!userInfo.affiliateProgram?.applicationsOpen)
  const affiliateLabel = affiliateSummary ? 'Affiliate' : 'Become an affiliate'
  const affiliateActive = location.pathname.startsWith('/affiliate')
  // Only hides links; the API (and the droplist service) check the rights themselves.
  const adminPages = ADMIN_PAGES.filter((page) => hasRole(userInfo.role, page.min))
  const adminLabel = hasRole(userInfo.role, 'admin') ? 'Admin' : 'Staff'

  return (
    <>
      <Row>
        <Logo src={logo} />
        <span
          onClick={() => {
            window.location.href = '/subscriptions';
          }}
        >
          <Button
            active={
              location.pathname === '/subscriptions' ||
              location.pathname === '/'
            }
          >
            Subscriptions
          </Button>
        </span>

        { showAffiliate &&
         <StyledLink to="/affiliate">
          <Button active={affiliateActive}>{affiliateLabel}</Button>
        </StyledLink>
        }
       
        {adminPages.length > 0 && <AdminMenu label={adminLabel} pages={adminPages} />}

        <StyledLink to="/settings">
          <Button active={location.pathname === '/settings'}>Settings</Button>
        </StyledLink>
        {
          authContext.userInfo.id && 
          <SignOutButton onClick={signOutHandler}>Sign Out</SignOutButton>
        }
        {
          !authContext.userInfo.id && 
          <SignOutButton onClick={loginHandler}>Login</SignOutButton>
        }
      </Row>
      <MobileRow>
        <MobileIconButton
          type="button"
          aria-label={modalNav ? 'Back' : 'Menu'}
          aria-expanded={modalNav ? undefined : navOpen}
          onClick={() =>
            modalNav && onClose ? onClose() : setNavOpen((prev) => !prev)
          }
        >
          <img src={modalNav ? backNavArrow : hamburgerIcon} alt="" />
        </MobileIconButton>
        <MobileLogo src={logo} alt="SwiftSole" />
        <MobileIconButton
          type="button"
          aria-label="Sign out"
          onClick={signOutHandler}
        >
          <img src={logOutIcon} alt="" />
        </MobileIconButton>
      </MobileRow>
      {navOpen && (
        <MobileNavPopout>
          <ForceRefesh
            $active={
              location.pathname === '/subscriptions' ||
              location.pathname === '/'
            }
            onClick={() => {
              window.location.href = '/subscriptions';
              setNavOpen(false);
            }}
          >
            Subscriptions
          </ForceRefesh>
          {showAffiliate && (
            <PopoutLink
              $active={affiliateActive}
              onClick={() => setNavOpen(false)}
              to="/affiliate"
            >
              {affiliateLabel}
            </PopoutLink>
          )}
          <PopoutLink
            $active={location.pathname === '/settings'}
            onClick={() => setNavOpen(false)}
            to="/settings"
          >
            Settings
          </PopoutLink>
          {adminPages.length > 0 && (
            <>
              <PopoutHeading>{adminLabel}</PopoutHeading>
              {adminPages.map((page) => (
                <PopoutLink
                  key={page.path}
                  $active={location.pathname.startsWith(page.path)}
                  onClick={() => setNavOpen(false)}
                  to={page.path}
                >
                  {page.label}
                </PopoutLink>
              ))}
            </>
          )}
        </MobileNavPopout>
      )}
    </>
  )
}

export default Nav
